import React, { useState } from 'react';
import { useGetUserExposureBetsQuery } from '../services/exposure/exposureApi';

const ClientExposureModal = ({ isOpen, onClose, userId, userName }) => {
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 20;

  // Use RTK Query hook to fetch exposure data
  const { data: response, isLoading, error: apiError } = useGetUserExposureBetsQuery(userId, {
    skip: !isOpen || !userId, // Skip query if modal is closed or no userId
  });

  const exposureData = response?.data || [];
  const error = apiError ? 'Error loading exposure data' : null;

  // Pagination logic
  const totalItems = exposureData.length;
  const totalPages = Math.ceil(totalItems / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const paginatedData = exposureData.slice(startIndex, startIndex + itemsPerPage);

  // Calculate totals for summary
  const totalExposure = exposureData.reduce((sum, item) => sum + (parseFloat(item.exposure) || 0), 0);
  const totalPotentialWinning = exposureData.reduce((sum, item) => sum + (parseFloat(item.potentialWinning) || 0), 0);

  if (!isOpen) return null;

  const formatDate = (dateString) => {
    if (!dateString) return '-';
    const date = new Date(dateString);
    const day = String(date.getDate()).padStart(2, '0');
    const month = date.toLocaleString('en-US', { month: 'short' });
    const year = date.getFullYear();
    let hours = date.getHours();
    const minutes = String(date.getMinutes()).padStart(2, '0');
    const seconds = String(date.getSeconds()).padStart(2, '0');
    const ampm = hours >= 12 ? 'PM' : 'AM';
    hours = hours % 12;
    hours = hours ? hours : 12; // the hour '0' should be '12'
    const formattedHours = String(hours).padStart(2, '0');
    return `${day} ${month} ${year} ${formattedHours}:${minutes}:${seconds} ${ampm}`;
  };

  const getRowStyle = (item) => {
    if (item.betType?.toLowerCase() === 'back') {
      return 'bg-blue-100';
    } else if (item.betType?.toLowerCase() === 'lay') {
      return 'bg-pink-100';
    }
    return 'bg-white';
  };


  return (
    <div className="fixed inset-0 z-50 overflow-y-auto">
      {/* Backdrop */}
      <div 
        className="fixed inset-0 bg-black/50 transition-opacity"
        onClick={onClose}
      />

      {/* Modal */}
      <div className="flex min-h-full items-center justify-center p-4">
        <div className="relative bg-white rounded shadow-xl max-w-6xl w-full max-h-[90vh] overflow-hidden flex flex-col">
          {/* Header */}
          <div className="bg-[#0F2327] px-6 py-4 flex items-center justify-between flex-shrink-0">
            <div>
              <h2 className="text-xl font-normal text-white">
                User Exposure - {userName}
              </h2>
              {exposureData.length > 0 && (
                <p className="text-sm text-gray-300 mt-1">
                  Total Exposure: <span className="font-semibold">{totalExposure.toFixed(2)}</span> | Total Potential Winning: <span className="font-semibold">{totalPotentialWinning.toFixed(2)}</span>
                </p>
              )}
            </div>
            <button
              onClick={onClose}
              className="text-white hover:text-gray-300 transition-colors text-2xl font-normal leading-none"
            >
              ×
            </button>
          </div>

          {/* Body */}
          <div className="overflow-y-auto flex-grow bg-white">
            {isLoading ? (
              <div className="flex items-center justify-center py-12">
                <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
              </div>
            ) : error ? (
              <div className="text-center py-12 text-red-600">
                {error}
              </div>
            ) : exposureData.length === 0 ? (
              <div className="text-center py-12 text-gray-500">
                No exposure data found
              </div>
            ) : (
              <div>
                <table className="exposure-table w-full border-collapse text-sm">
                  <thead>
                    <tr className="bg-black text-white sticky top-0">
                      <th className="p-2 text-left font-semibold border-r border-gray-400">Type</th>
                      <th className="p-2 text-left font-semibold border-r border-gray-400">Event Name</th>
                      <th className="p-2 text-left font-semibold border-r border-gray-400">Market/Fancy Name</th>
                      <th className="p-2 text-left font-semibold border-r border-gray-400">Bet Type</th>
                      <th className="p-2 text-left font-semibold border-r border-gray-400">Stake</th>
                      <th className="p-2 text-left font-semibold border-r border-gray-400">Odds/Size</th>
                      <th className="p-2 text-left font-semibold border-r border-gray-400">Exposure</th>
                      <th className="p-2 text-left font-semibold border-r border-gray-400">Potential Win</th>
                      <th className="p-2 text-left font-semibold">Placed At</th>
                    </tr>
                  </thead>
                  <tbody>
                    {paginatedData.map((item, index) => (
                      <tr key={index} className={`${getRowStyle(item)} border-b border-gray-300 hover:bg-opacity-75`}>
                        <td className="p-2 border-r border-gray-300">
                          <span className="uppercase text-xs font-semibold">{item.type}</span>
                        </td>
                        <td className="p-2 border-r border-gray-300 font-medium">{item.eventName || '-'}</td>
                        <td className="p-2 border-r border-gray-300">
                          {item.type === 'bookmaker' ? (
                            <div>
                              <div className="text-blue-600 font-medium">{item.team || '-'}</div>
                              {item.marketName && <div className="text-xs text-gray-600">{item.marketName}</div>}
                            </div>
                          ) : item.type === 'casino' ? (
                            <div className="font-medium">{item.team || '-'}</div>
                          ) : (
                            <div className="font-medium">{item.fancyName || '-'}</div>
                          )}
                        </td>
                        <td className="p-2 border-r border-gray-300">
                          <span className={`font-medium uppercase text-xs ${
                            item.betType?.toLowerCase() === 'back' 
                              ? 'text-blue-600' 
                              : 'text-red-600'
                          }`}>
                            {item.type === 'fancy' 
                              ? (item.betType?.toLowerCase() === 'back' ? 'Yes' : 'No')
                              : (item.betType?.toLowerCase() === 'back' ? 'Lagai' : 'Khai')
                            }
                          </span>
                        </td>
                        <td className="p-2 border-r border-gray-300 font-semibold">{item.stake || '-'}</td>
                        <td className="p-2 border-r border-gray-300">
                          {item.type === 'bookmaker' || item.type === 'casino' ? (
                            <span>{item.odds || '-'}</span>
                          ) : (
                            <div>
                              <div>{item.size ? `Size: ${item.size}` : '-'}</div>
                              {item.runs && <div className="text-xs text-gray-600">Runs: {item.runs}</div>}
                            </div>
                          )}
                        </td>
                        <td className="p-2 border-r border-gray-300 font-semibold text-red-600">{parseFloat(item.exposure).toFixed(2) || '-'}</td>
                        <td className="p-2 border-r border-gray-300 font-semibold text-green-600">{parseFloat(item.potentialWinning).toFixed(2) || '-'}</td>
                        <td className="p-2 text-xs text-gray-600">{formatDate(item.placedAt)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>

                {/* Pagination Controls */}
                {totalPages > 1 && (
                  <div className="flex items-center justify-between p-4 border-t border-gray-300 bg-gray-50 flex-shrink-0">
                    <button
                      onClick={() => setCurrentPage(Math.max(1, currentPage - 1))}
                      disabled={currentPage === 1}
                      className="px-4 py-2 bg-blue-600 text-white rounded disabled:bg-gray-400 disabled:cursor-not-allowed hover:bg-blue-700 transition-colors"
                    >
                      Previous
                    </button>
                    <span className="text-gray-700 font-medium">
                      Page {currentPage} of {totalPages} ({totalItems} items)
                    </span>
                    <button
                      onClick={() => setCurrentPage(Math.min(totalPages, currentPage + 1))}
                      disabled={currentPage === totalPages}
                      className="px-4 py-2 bg-blue-600 text-white rounded disabled:bg-gray-400 disabled:cursor-not-allowed hover:bg-blue-700 transition-colors"
                    >
                      Next
                    </button>
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default ClientExposureModal;
